import React, { memo, useMemo, useRef, useEffect } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import HeartAnimation from '../lottie/fav.json';
import HeartOpenAnimation from '../lottie/heart-open.json';
import BadgeAnimation from '../lottie/badge.json';

interface AnimatedHeartIconProps {
    isFavorited: boolean;
    size?: number;
    speed?: number;
    showUnfavorited?: boolean;
    onClick?: () => void;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    title?: string;
    loop?: boolean;
    scale?: number;
    animationType?: 'heart' | 'badge';
}

const AnimatedHeartIcon: React.FC<AnimatedHeartIconProps> = memo(({
    isFavorited,
    size = 50,
    speed = 0.375,
    showUnfavorited = false,
    onClick,
    disabled = false,
    className = '',
    style = {},
    title,
    loop = true,
    scale = 1.2,
    animationType = 'heart'
}) => {
    const lottieRef = useRef<LottieRefCurrentProps>(null);

    // Memoize animation data to prevent recreation on every render
    const animationData = useMemo(() => {
        if (animationType === 'badge') {
            return BadgeAnimation;
        } else {
            return isFavorited ? HeartAnimation : HeartOpenAnimation;
        }
    }, [isFavorited, animationType]);

    // Set speed when component mounts or speed changes
    useEffect(() => {
        if (lottieRef.current) {
            lottieRef.current.setSpeed(speed);
        }
    }, [speed]);

    // Memoize default style to prevent recreation on every render
    const defaultStyle: React.CSSProperties = useMemo(() => ({
        margin: 0,
        transition: 'all 0.3s ease',
        transform: isFavorited ? `scale(${scale})` : 'scale(1)',
        width: size,
        height: size,
        ...style
    }), [isFavorited, style, scale, size]);

    // Memoize container props to prevent recreation on every render
    const containerProps = useMemo(() => ({
        className: `${onClick ? 'cursor-pointer' : ''} ${className}`,
        onClick: onClick && !disabled ? onClick : undefined,
        title: title || (isFavorited ? 'Favorited' : 'Not favorited'),
        style: onClick ? { transition: 'all 0.3s ease' } : {}
    }), [onClick, disabled, className, title, isFavorited]);

    // Don't render if not favorited and showUnfavorited is false
    if (!isFavorited && !showUnfavorited) {
        return null;
    }

    return (
        <div {...containerProps}>
            <Lottie
                lottieRef={lottieRef}
                animationData={animationData}
                loop={loop}
                autoplay={true}
                style={defaultStyle}
            />
        </div>
    );
});

// Add display name for debugging
AnimatedHeartIcon.displayName = 'AnimatedHeartIcon';

export default AnimatedHeartIcon; 